// Pure logic for the Qualifications registry (council-maintained specialized
// roles/certifications — NCAP, Shooting Sports, ...). No Firebase — takes
// already-loaded docs. See the QualificationArea/QualificationAssignment
// doc comments in types.ts for the data model.
//
// This module is also where a future event/Initiative workflow will import
// isQualificationValidOn() from to answer "who holds a valid X qualification
// as of date D" — that integration isn't built in this increment, but the
// function it needs already lives here, pure and already tested.

import type { Tone } from './unitHealth.ts';
import type {
  ISODateString,
  QualificationArea,
  QualificationAssignment,
  QualificationRoleType,
  QualificationTypeDef,
} from '../types';

/** Same 60-day "approaching expiration" window Compass already uses for SYT/
 *  Hazardous Weather/recharter — reused here for visual consistency rather
 *  than inventing a Qualifications-specific threshold. */
export const QUALIFICATION_EXPIRING_SOON_DAYS = 60;

export type QualificationExpirationStatus = 'current' | 'expiringSoon' | 'expired' | 'noExpiration';

export interface QualificationExpiration {
  status: QualificationExpirationStatus;
  tone: Tone | null;
  /** Days until expiration (negative once expired). Null when there's no
   *  expiration date to measure from. */
  days: number | null;
}

/** Compass currently uses the viewer's local calendar (no council timezone
 * setting exists). Extract today locally, never through toISOString()/UTC.
 * Callers with an explicit event/council date can pass that date to the classifier. */
export function qualificationLocalDate(now = new Date()): ISODateString {
  return `${String(now.getFullYear()).padStart(4, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** Gregorian calendar-day ordinal, not a timestamp. No timezone or DST enters
 * the difference; inputs are the canonical YYYY-MM-DD date-only values. */
function calendarDay(iso: ISODateString): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) throw new RangeError('Invalid qualification calendar date');
  const [year, month, day] = iso.split('-').map(Number);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const lengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > lengths[month - 1]) throw new RangeError('Invalid qualification calendar date');
  const prior = year - 1;
  return prior * 365 + Math.floor(prior / 4) - Math.floor(prior / 100) + Math.floor(prior / 400)
    + lengths.slice(0, month - 1).reduce((sum, length) => sum + length, 0) + day;
}

/** Classifies one assignment's expiration state "as of now" for display —
 *  current / approaching expiration / expired / no expiration set. Mirrors
 *  YPT_TONE's tone vocabulary (ok/amber/red) so the UI reads consistently
 *  with the rest of Unit Health. */
export function qualificationExpiration(
  expiresOn: ISODateString | null | undefined,
  asOf: ISODateString = qualificationLocalDate(),
): QualificationExpiration {
  if (!expiresOn) return { status: 'noExpiration', tone: null, days: null };
  const days = calendarDay(expiresOn) - calendarDay(asOf);
  if (days < 0) return { status: 'expired', tone: 'red', days };
  if (days <= QUALIFICATION_EXPIRING_SOON_DAYS) return { status: 'expiringSoon', tone: 'amber', days };
  return { status: 'current', tone: 'ok', days };
}

/** Is this assignment valid ON a specific date (not "as of now") — the
 *  question a future event workflow actually needs to ask ("valid for the
 *  date of this event"), distinct from qualificationExpiration()'s
 *  right-now display classification above. No expiration date means always
 *  valid. Uses the same date-only classifier as the roster, including the
 *  entire expiration date. */
export function isQualificationValidOn(
  a: Pick<QualificationAssignment, 'expiresOn'>,
  dateISO: ISODateString,
): boolean {
  return qualificationExpiration(a.expiresOn, dateISO).status !== 'expired';
}

/** One person's held roles/qualifications within a single area — the shape
 *  the UI renders one roster row from. */
export interface PersonAreaAssignments {
  personId: string;
  roles: QualificationAssignment[];
  qualifications: QualificationAssignment[];
}

/** Groups an area's assignments by person — the "area first, people
 *  underneath" structure the UI is built around, never a flat person-
 *  centric list. Callers filter `assignments` to one areaId first (the
 *  hook already scopes by tenant; area scoping happens here since a
 *  person's assignments across DIFFERENT areas must never be merged into
 *  one row). */
export function groupAssignmentsByPerson(assignments: QualificationAssignment[]): PersonAreaAssignments[] {
  const byPerson = new Map<string, PersonAreaAssignments>();
  for (const a of assignments) {
    let entry = byPerson.get(a.personId);
    if (!entry) {
      entry = { personId: a.personId, roles: [], qualifications: [] };
      byPerson.set(a.personId, entry);
    }
    (a.kind === 'role' ? entry.roles : entry.qualifications).push(a);
  }
  return [...byPerson.values()];
}

/** Role/qualification type ids this person already holds in this area —
 *  used to exclude already-held types from the "add" picker (each type is
 *  independently grantable, so this is the only thing that should ever
 *  disable an option there). */
export function heldTypeIds(entry: PersonAreaAssignments | undefined, kind: 'role' | 'qualification'): Set<string> {
  const list = kind === 'role' ? entry?.roles : entry?.qualifications;
  return new Set((list ?? []).map((a) => a.typeId));
}

export function roleTypeName(area: QualificationArea, typeId: string): string {
  return area.roleTypes.find((t) => t.id === typeId)?.name ?? 'Unknown role';
}

export function qualificationTypeName(area: QualificationArea, typeId: string): string {
  return area.qualificationTypes.find((t) => t.id === typeId)?.name ?? 'Unknown qualification';
}

export function findRoleType(area: QualificationArea, typeId: string): QualificationRoleType | undefined {
  return area.roleTypes.find((t) => t.id === typeId);
}

export function findQualificationType(area: QualificationArea, typeId: string): QualificationTypeDef | undefined {
  return area.qualificationTypes.find((t) => t.id === typeId);
}
